import firebase from 'firebase/app';
import 'firebase/firestore';

/**
 * Is this user a Strike admin?
 *
 * NEVER throws. "I could not read the admin list" and "you are not in the admin
 * list" are the same answer to the caller — no.
 *
 * That distinction matters because the admin list is not readable by non-admins:
 * clients/strike/admins/{uid} sits under the clients/{clientId}/{document=**}
 * rule, which requires ownership or admin. So a normal user asking whether they
 * are an admin gets a permission error, and the correct reading of that error is
 * "no", not "something is broken".
 *
 * Treating it as fatal locked every non-admin out of every team page: the team
 * guard ran this inside a Promise.all, the rejection propagated, and the catch
 * denied access before membership was ever checked.
 *
 * Failing closed is also the safe direction. An unreadable admin list can only
 * ever downgrade someone to a normal user; it can never grant admin.
 */
export async function fetchIsStrikeAdmin(uid) {
  if (!uid) return false;
  try {
    const snapshot = await firebase
      .firestore()
      .collection('clients')
      .doc('strike')
      .collection('admins')
      .doc(uid)
      .get();
    // role === 'Admin', not merely "a document exists". This matches isAdmin() in
    // firestore.rules, so the client and the server agree on who is an admin — a
    // doc with any other role would fail every admin rule anyway.
    return snapshot.exists && (snapshot.data() || {}).role === 'Admin';
  } catch (err) {
    // Expected for every normal user. Logged at debug volume, not as an error,
    // so it does not read as a fault in the console of every non-admin.
    console.debug('[fetchIsStrikeAdmin] treating unreadable admin list as not-admin:', err && err.code);
    return false;
  }
}

export default fetchIsStrikeAdmin;
